import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast, Toaster } from "sonner";
import Navbar from "@/components/Navbar";
import { errMsg } from "@/lib/errors";
import { ShieldCheck } from "lucide-react";

function dashboardFor(role) {
  return role === "admin" ? "/admin" : role === "provider" ? "/provider" : "/customer";
}

function roleCanAccess(path, role) {
  if (!path) return true;
  if (path.startsWith("/admin") || path.startsWith("/owner")) return role === "admin";
  if (path.startsWith("/provider")) return role === "provider";
  if (path.startsWith("/customer") || path.startsWith("/book/")) return role === "customer";
  return true; // public/other paths
}

export default function Login() {
  const { login, user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [sp] = useSearchParams();
  const redirectTo = sp.get("redirect") || "";
  const wrongRoleSwitch = sp.get("switch") === "1";
  const isOwnerLogin = loc.pathname.startsWith("/owner/login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // Once we have a session, decide where to send the user. This runs after a fresh
  // login too (submit() sets `user` and relies on this effect for navigation).
  // Key rule: if the current role CAN access the intended redirect target, always
  // navigate — regardless of whether `switch=1` is still on the URL. Only when the
  // target is inaccessible with this role do we surface the "different account
  // needed" banner.
  useEffect(() => {
    if (!user) return;
    // On the dedicated Owner sign-in, non-admin sessions get an explicit banner
    // instead of being silently routed to a customer/provider dashboard.
    if (isOwnerLogin && user.role !== "admin") {
      toast.error(`Signed in as ${user.role} — this account is not an owner/admin.`);
      return;
    }
    if (roleCanAccess(redirectTo, user.role)) {
      nav(redirectTo || (isOwnerLogin ? "/admin" : dashboardFor(user.role)), { replace: true });
      return;
    }
    if (wrongRoleSwitch) {
      toast.message(`Signed in as ${user.role}. Please log in with a different account to access ${redirectTo || "that page"}.`);
    } else {
      nav(dashboardFor(user.role), { replace: true });
    }
  }, [user, wrongRoleSwitch, redirectTo, nav, isOwnerLogin]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      // useEffect above handles navigation once `user` is set, using roleCanAccess.
    } catch (err) {
      toast.error(errMsg(err, "Login failed"));
    } finally { setBusy(false); }
  };

  // Owner-branded variant of the sign-in page. Reuses the exact same submit path,
  // so nothing changes on the backend — only the copy and post-login default route.
  if (isOwnerLogin) {
    return (
      <div className="min-h-screen bg-slate-950 text-white">
        <Toaster position="top-center" />
        <div className="max-w-md mx-auto px-4 py-16">
          <div className="flex items-center gap-2 mb-6">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-500 text-white">
              <ShieldCheck size={18} />
            </span>
            <div>
              <div className="font-display font-extrabold text-white">FixNear</div>
              <div className="text-[10px] tracking-widest uppercase text-emerald-400">Owner Control</div>
            </div>
          </div>
          <h1 className="font-display text-3xl font-bold text-white">Owner sign in</h1>
          <p className="text-slate-400 mt-2">Sign in with your owner/admin credentials to open the Control Panel.</p>

          <form onSubmit={submit} className="mt-6 bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Owner email</label>
              <input data-testid="owner-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-3 text-white outline-none focus:border-emerald-500" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Password</label>
              <input data-testid="owner-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-3 text-white outline-none focus:border-emerald-500" />
            </div>
            <button data-testid="owner-login-submit" disabled={busy}
              className="w-full rounded-full font-bold text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 h-11">
              {busy ? "Signing in…" : "Sign in to Owner Panel"}
            </button>
            <div className="text-[11px] text-slate-500 text-center">
              Owner-only access. Non-admin sessions will be blocked.
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Toaster position="top-center" />
      <div className="max-w-md mx-auto px-4 py-14">
        <h1 className="font-display text-3xl font-bold text-slate-900">Welcome back</h1>
        <p className="text-slate-600 mt-2">Log in to manage your bookings.</p>

        {wrongRoleSwitch && user && !roleCanAccess(redirectTo, user.role) && (
          <div data-testid="switch-role-banner" className="mt-6 card !p-4 border-amber-200 bg-amber-50">
            <div className="font-semibold text-amber-900">Different account needed</div>
            <div className="text-sm text-amber-900/80 mt-1">
              You're signed in as <b>{user.role}</b> ({user.email}). Log out and sign back in with an admin account to open <code className="bg-amber-100 rounded px-1">{redirectTo || "the requested page"}</code>.
            </div>
            <button
              data-testid="switch-logout-btn"
              onClick={() => { logout(); toast.success("Signed out — log in with your admin account"); }}
              className="btn-outline mt-3 !py-2"
            >
              Log out
            </button>
          </div>
        )}

        <form onSubmit={submit} className="mt-6 card space-y-4">
          <div>
            <label className="label">Email</label>
            <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="field" />
          </div>
          <div>
            <label className="label">Password</label>
            <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="field" />
          </div>
          <button data-testid="login-submit" disabled={busy} className="btn-accent w-full">{busy ? "Logging in…" : "Log in"}</button>
          <div className="text-sm text-center">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-emerald-600 font-semibold">Forgot password?</Link>
          </div>
          <div className="text-sm text-slate-600 text-center">
            No account? <Link to="/signup" className="text-emerald-600 font-semibold">Sign up</Link>
          </div>
          <div className="text-xs text-slate-400 text-center pt-1 border-t border-slate-100">
            Are you the FixNear owner? <Link to="/owner/login" data-testid="owner-login-link" className="text-slate-700 font-semibold underline">Owner sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
